import React, { useState } from 'react'
import { Plus, Trash2, ChevronUp, ChevronDown, Pencil, GripVertical } from 'lucide-react'
import { useLessonBuilder } from '../store/useLessonBuilder'
import { getCardDef } from '../cards/registry'
import type { CardType } from '../types/LessonDoc'

// Canvas giữa: mỗi PHẦN là vùng thả thẻ; thẻ trong phần kéo được + sửa nội dung inline.
const SectionCanvas: React.FC = () => {
  const {
    lesson,
    selectedCardId,
    addSection,
    renameSection,
    removeSection,
    moveSection,
    addCard,
    updateCard,
    removeCard,
    moveCard,
    moveCardToSection,
    selectCard,
  } = useLessonBuilder()
  const [dragOver, setDragOver] = useState<string | null>(null)

  const handleDrop = (sectionId: string, e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(null)
    const newType = e.dataTransfer.getData('text/card-new')
    if (newType) {
      addCard(sectionId, newType as CardType)
      return
    }
    const moveId = e.dataTransfer.getData('text/card-move')
    if (moveId) moveCardToSection(moveId, sectionId)
  }

  return (
    <div className="space-y-4">
      {lesson.sections.map((sec, si) => (
        <div
          key={sec.id}
          onDragOver={(e) => {
            e.preventDefault()
            setDragOver(sec.id)
          }}
          onDragLeave={() => setDragOver((d) => (d === sec.id ? null : d))}
          onDrop={(e) => handleDrop(sec.id, e)}
          className={`rounded-2xl border-2 p-4 transition-colors ${
            dragOver === sec.id ? 'border-blue-400 bg-blue-50/50' : 'border-gray-100 bg-white'
          }`}
        >
          {/* Header phần */}
          <div className="flex items-center gap-2 mb-3">
            <span className="w-7 h-7 rounded-full bg-blue-600 text-white text-sm font-bold flex items-center justify-center shrink-0">
              {si + 1}
            </span>
            <input
              value={sec.title}
              onChange={(e) => renameSection(sec.id, e.target.value)}
              className="flex-1 min-w-0 bg-transparent text-base font-black text-blue-900 outline-none border-b border-transparent focus:border-blue-200"
            />
            <button onClick={() => moveSection(sec.id, -1)} disabled={si === 0} className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30">
              <ChevronUp size={16} />
            </button>
            <button onClick={() => moveSection(sec.id, 1)} disabled={si === lesson.sections.length - 1} className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30">
              <ChevronDown size={16} />
            </button>
            <button
              onClick={() => {
                if (sec.cards.length === 0 || confirm('Xoá phần này và các thẻ bên trong?')) removeSection(sec.id)
              }}
              className="p-1 text-gray-400 hover:text-red-600"
            >
              <Trash2 size={16} />
            </button>
          </div>

          {/* Thẻ trong phần */}
          {sec.cards.length === 0 ? (
            <div className="py-8 text-center text-sm text-gray-400 border-2 border-dashed border-gray-200 rounded-xl">
              Kéo thẻ từ kho bên trái thả vào đây
            </div>
          ) : (
            <div className="space-y-2">
              {sec.cards.map((card, ci) => {
                const def = getCardDef(card.type)
                const Icon = def.icon
                const open = card.id === selectedCardId
                const Editor = def.Editor
                return (
                  <div
                    key={card.id}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData('text/card-move', card.id)
                      e.dataTransfer.effectAllowed = 'move'
                    }}
                    className={`rounded-xl border ${open ? 'border-blue-300' : 'border-gray-100'} bg-white`}
                  >
                    <div className="flex items-center gap-2 p-2.5">
                      <GripVertical size={16} className="text-gray-300 cursor-grab active:cursor-grabbing shrink-0" />
                      <span className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${def.color}`}>
                        <Icon size={15} />
                      </span>
                      <button onClick={() => selectCard(open ? null : card.id)} className="flex-1 min-w-0 text-left">
                        <span className="block text-sm font-semibold text-gray-800 truncate">{card.title || def.label}</span>
                        <span className="block text-[11px] text-gray-400">{def.label}</span>
                      </button>
                      <button onClick={() => selectCard(open ? null : card.id)} className="p-1 text-gray-400 hover:text-blue-600">
                        <Pencil size={15} />
                      </button>
                      <button onClick={() => moveCard(card.id, -1)} disabled={ci === 0} className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30">
                        <ChevronUp size={15} />
                      </button>
                      <button onClick={() => moveCard(card.id, 1)} disabled={ci === sec.cards.length - 1} className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30">
                        <ChevronDown size={15} />
                      </button>
                      <button onClick={() => removeCard(card.id)} className="p-1 text-gray-400 hover:text-red-600">
                        <Trash2 size={15} />
                      </button>
                    </div>

                    {/* Trình sửa nội dung inline */}
                    {open && (
                      <div className="border-t border-gray-100 p-3 space-y-3 bg-gray-50/40">
                        <div>
                          <label className="text-xs font-bold text-gray-500 uppercase">Tiêu đề thẻ (tuỳ chọn)</label>
                          <input
                            className="w-full p-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-200"
                            value={card.title || ''}
                            onChange={(e) => updateCard(card.id, { title: e.target.value })}
                          />
                        </div>
                        <Editor card={card} onChange={(patch) => updateCard(card.id, patch)} />
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      ))}

      <button
        onClick={() => addSection()}
        className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl border-2 border-dashed border-gray-200 text-gray-500 font-semibold hover:border-blue-300 hover:text-blue-600"
      >
        <Plus size={18} /> Thêm phần
      </button>
    </div>
  )
}

export default SectionCanvas
